import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Tabs,
  Row,
  Typography,
  TabsProps,
  Button, InputNumber,
} from 'antd';
import usePutRequestUpgrade from '../../hooks/utils/usePutRequestUpgrade';
import useToastContext from '../../hooks/useToastContext';
import { validateStatus } from '../../utils/utils';
import { SectionTitle, SectionTitleContainer } from '../styles';
import SourceExclusions from './SourceExclusions';

const parseILoad = (data: ICashOutAndLoad, loadSourceExclusions?: string[]): ICashOutAndLoad => ({
  ...data,
  max_amount: Number(data?.max_amount),
  max_daily_amount: Number(data?.max_daily_amount),
  max_monthly_amount: Number(data?.max_monthly_amount),
  min_amount: Number(data?.min_amount),
  source_exclusions: loadSourceExclusions
});

const parseICashOut = (data: ICashOutAndLoad, cashOutSourceExclusions?: string[]): ICashOutAndLoad => ({
  ...data,
  max_amount: Number(data?.max_amount),
  max_daily_amount: Number(data?.max_daily_amount),
  max_monthly_amount: Number(data?.max_monthly_amount),
  min_amount: Number(data?.min_amount),
  source_exclusions: cashOutSourceExclusions
});

const parseICashback = (data: ICashback): ICashback => ({
  ...data,
  max_amount: Number(data?.max_amount),
  min_amount: Number(data?.min_amount),
});

const parseIPurchase = (data: IPurchase): IPurchase => ({
  ...data,
  min_amount: Number(data?.min_amount),
});

const parseLimits = (limits: LimitsFormInputs, cashOutSourceExclusions?: string[], loadSourceExclusions?: string[]): LimitsFormInputs => ({
  ...limits,
  cash_out: parseICashOut(limits?.cash_out, cashOutSourceExclusions),
  cashback: parseICashback(limits?.cashback),
  load: parseILoad(limits?.load, loadSourceExclusions),
  purchase: parseIPurchase(limits?.purchase),
});

export type ICashOutAndLoad = {
  // eslint-disable-next-line camelcase
  max_amount: number;
  // eslint-disable-next-line camelcase
  max_daily_amount: number;
  // eslint-disable-next-line camelcase
  max_monthly_amount: number;
  // eslint-disable-next-line camelcase
  min_amount: number;
  // eslint-disable-next-line camelcase
  source_exclusions: string[];
}

export type ICashback = {
  // eslint-disable-next-line camelcase
  max_amount: number;
  // eslint-disable-next-line camelcase
  min_amount: number;
}

export type IPurchase = {
  // eslint-disable-next-line camelcase
  min_amount: number;
}

export type LimitsFormInputs = {
  // eslint-disable-next-line camelcase
  cash_out: ICashOutAndLoad
  cashback: ICashback
  load: ICashOutAndLoad
  purchase: IPurchase
}


const CashOutForm = ({
 action,
 sourceExclusions,
 setSourceExclusions
}: {
  action: string,
  sourceExclusions: string[],
  setSourceExclusions: (sourceExclusions: string[]) => void;
}) => {

  return (
    <>
      <Row gutter={24}>
          <Col span={6}>
            <Form.Item<LimitsFormInputs>
              name={['cash_out', 'min_amount']}
              label={'Monto minímo'}
              rules={[
                { required: false, message: 'Por favor ingresar un número.' },
                { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
              ]}
            >
              <InputNumber<number>
                style={{ width: '100%' }}
                disabled={action === 'view'}
                controls={false}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                }
                parser={(value) =>
                  value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
                }
              />

            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item<LimitsFormInputs>
              name={['cash_out', 'max_amount']}
              label={'Monto máximo'}
              rules={[
                { required: false, message: 'Por favor ingresar un número.' },
                { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
              ]}
            >
              <InputNumber<number>
                style={{ width: '100%' }}
                disabled={ action === 'view'}
                controls={false}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                }
                parser={(value) =>
                  value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item<LimitsFormInputs>
              name={['cash_out', 'max_daily_amount']}
              label={'Monto diario máximo'}
              rules={[
                { required: false, message: 'Por favor ingresar un número.' },
                { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
              ]}
            >
              <InputNumber<number>
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                }
                parser={(value) =>
                  value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
                }
                style={{ width: '100%' }}
                disabled={ action === 'view'}
                controls={false}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item<LimitsFormInputs>
              name={['cash_out', 'max_monthly_amount']}
              label={'Monto mensual máximo'}
              rules={[
                { required: false, message: 'Por favor ingresar un número.' },
                { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
              ]}
            >
              <InputNumber<number>
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                }
                parser={(value) =>
                  value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
                }
                style={{ width: '100%' }}
                disabled={ action === 'view'}
                controls={false}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <SectionTitleContainer>
              <SectionTitle> Source exclusions </SectionTitle>
            </SectionTitleContainer>
            <SourceExclusions
              action={action}
              sourceExclusions={ sourceExclusions }
              setSourceExclusions={ setSourceExclusions }
            />
          </Col>
      </Row>
    </>
  )
};

const Loads = ({
  action,
  sourceExclusions,
  setSourceExclusions
}: {
  action: string,
  sourceExclusions: string[],
  setSourceExclusions: (sourceExclusions: string[]) => void;
}) => {

  return (
    <>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['load', 'min_amount']}
            label={'Monto minímo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['load', 'max_amount']}
            label={'Monto máximo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['load', 'max_daily_amount']}
            label={'Monto diario máximo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['load', 'max_monthly_amount']}
            label={'Monto mensual máximo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <SectionTitleContainer>
            <SectionTitle> Source exclusions </SectionTitle>
          </SectionTitleContainer>
          <SourceExclusions
            action={action}
            sourceExclusions={ sourceExclusions }
            setSourceExclusions={ setSourceExclusions }
          />
        </Col>
      </Row>
    </>
  )
}

const Cashback = ({ action }) => {

  return (
    <>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['cashback', 'min_amount']}
            label={'Monto minímo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['cashback', 'max_amount']}
            label={'Monto maximo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}

const Purchase = ({ action }) => {
  return (
    <>
      <Row gutter={24}>
        <Col span={6}>
          <Form.Item<LimitsFormInputs>
            name={['purchase', 'min_amount']}
            label={'Monto minímo'}
            rules={[
              { required: false, message: 'Por favor ingresar un número.' },
              { type: 'number', min: 0, message: 'Ingrese un número mayor a cero.' },
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              parser={(value) =>
                value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
              }
              style={{ width: '100%' }}
              disabled={ action === 'view'}
              controls={false}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}

const Limits = ({
  cardAffinityGroupId,
  limit,
  setLimit,
  action,
  submitForm
}: {
  cardAffinityGroupId?: string | undefined,
  limit?: LimitsFormInputs | undefined,
  setLimit?: (value: LimitsFormInputs) => void;
  action?: string | undefined;
  setNextStep?: (value: boolean) => void;
  submitForm?: boolean | undefined;
}) => {
  const [formLimit] = Form.useForm();
  const [submittable, setSubmittable] = useState(false);
  const { putError, putLoading, executePut } = usePutRequestUpgrade({ path: `v1/card_affinity_groups/${cardAffinityGroupId}/transactions/limits` });
  const toast = useToastContext();
  const [cashOutSourceExclusions, setCashOutSourceExclusions] = useState<string[]>([]);
  const [loadSourceExclusions, setLoadSourceExclusions] = useState<string[]>([]);

  const onSubmitLimit = async (data: LimitsFormInputs) => {
    const dataWithSourceExclusions = {
      load: {
        ...data?.load,
        source_exclusions: loadSourceExclusions
      },
      cash_out: {
        ...data?.cash_out,
        source_exclusions: cashOutSourceExclusions
      },
      purchase: data?.purchase,
      cashback: data?.cashback
    };

    if(cardAffinityGroupId !== undefined && cardAffinityGroupId !== null ) {
      const finalParsedLimits = parseLimits(data, cashOutSourceExclusions, loadSourceExclusions);
      try {
        const response = await executePut({ data: finalParsedLimits });

        if (validateStatus(response?.status)) {
          toast.success('Exito', 'Limites actualizados correctamente!.');
          setLimit(dataWithSourceExclusions);
        } else {
          throw new Error();
        }
      } catch (e: unknown) {
        toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
      }
    } else {
      setLimit(dataWithSourceExclusions);
    }
  }

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'CashOut',
      children: <CashOutForm
        action={action}
        sourceExclusions={cashOutSourceExclusions}
        setSourceExclusions={setCashOutSourceExclusions}
      />,
    },
    {
      key: '2',
      label: 'CashBack',
      children: <Cashback action={action} />,
    },
    {
      key: '3',
      label: 'Cargas',
      children: <Loads
        action={action}
        sourceExclusions={loadSourceExclusions}
        setSourceExclusions={setLoadSourceExclusions}
      />,
    },
    {
      key: '4',
      label: 'Compras',
      children: <Purchase action={action} />,
    },
  ];

  useEffect(() => {
    if(submitForm) {
      formLimit.setFieldValue(['cash_out', 'source_exclusions'], cashOutSourceExclusions);
      formLimit.setFieldValue(['load', 'source_exclusions'], loadSourceExclusions);
      formLimit.submit();
    }
  }, [submitForm]);

  useEffect(() => {
    if(limit !== undefined && limit !== null ) {
      formLimit.setFieldValue(['cash_out', 'max_amount'], limit?.cash_out?.max_amount);
      formLimit.setFieldValue(['cash_out', 'min_amount'], limit?.cash_out?.min_amount);
      formLimit.setFieldValue(['cash_out', 'max_monthly_amount'], limit?.cash_out?.max_monthly_amount);
      formLimit.setFieldValue(['cash_out', 'max_daily_amount'], limit?.cash_out?.max_daily_amount);
      formLimit.setFieldValue(['cash_out', 'source_exclusions'], limit?.cash_out?.source_exclusions);
      setCashOutSourceExclusions(limit?.cash_out?.source_exclusions);
      formLimit.setFieldValue(['load', 'max_daily_amount'], limit?.load?.max_daily_amount);
      formLimit.setFieldValue(['load', 'min_amount'], limit?.load?.min_amount);
      formLimit.setFieldValue(['load', 'max_monthly_amount'], limit?.load?.max_monthly_amount);
      formLimit.setFieldValue(['load', 'max_amount'], limit?.load?.max_amount);
      formLimit.setFieldValue(['load', 'source_exclusions'], limit?.load?.source_exclusions);
      setLoadSourceExclusions(limit?.load?.source_exclusions);
      formLimit.setFieldValue(['cashback', 'max_amount'], limit?.cashback?.max_amount);
      formLimit.setFieldValue(['cashback', 'min_amount'], limit?.cashback?.min_amount);
      formLimit.setFieldValue(['purchase', 'min_amount'], limit?.purchase?.min_amount);
    }
  }, [limit]);

  return (
    <Form<LimitsFormInputs>
      name="basic"
      layout="vertical"
      form={ formLimit }
      onFinish={ onSubmitLimit }
      onFinishFailed={console.log}
      style={{ maxWidth: '100%' }}
    >
      <Typography.Title level={3}>Limites</Typography.Title>
      <Tabs
        style={{justifyContent: 'left', marginLeft: 'none' }}
        tabPosition={'left'}
        defaultActiveKey="1"
        items={items}
        size={'large'}
      />
      {
        (action !== 'view' && cardAffinityGroupId !== undefined) ? (
          <Row gutter={24}>
            <Col span={24} style={{ textAlign: 'right' }} >
              <Button
                type="primary"
                htmlType="submit"
                disabled={ action !== 'edit' && action !== 'view' ? !submittable : false }
                loading={ putLoading }
              >
                Guardar
              </Button>
            </Col>
          </Row>
        ) : ''
      }

    </Form>
  )
}
export default Limits;